import { v4 as uuidv4 } from 'uuid';

import {
  BaseCartValue,
  RestaurantCartItem,
  RestaurantCartValue,
  RetailCartItem,
  RetailCartValue,
} from '@/models/cart/types';
import { OrderHistoryItem } from '@/types/orders';
import { getFromLocalStorage, saveToLocalStorage } from '@/utils/localStorageUtils';
import { LocalStorageKey } from '@/utils/storageKeys';
import { CartItemType } from '@/constants/enum';

/**
 * Maps an order history item to a cart entry, explicitly normalising the
 * API field names (note → specialInstructions, modifierGroupId → groupId).
 * The cartType parameter sets the required discriminant property so the
 * resulting value satisfies RetailCartValue or RestaurantCartValue exactly.
 */
export function mapItemToCartEntry(
  item: OrderHistoryItem,
  cartType: CartItemType.RETAIL | CartItemType.RESTAURANT,
  options?: { menuId?: string },
): RetailCartItem | RestaurantCartItem {
  const cartId = uuidv4();
  const mappedModifiers = (item.modifiers ?? []).map(m => ({
    ...m,
    groupId: m.modifierGroupId,
  }));

  const total = (item.price ?? 0) + (item.modifiers ?? []).reduce(
    (acc, modifier) => {
      const quantity = modifier.quantity ?? 1;
      const price = modifier.price ?? 0;
      const modifierTotal = quantity * price;

      return acc + modifierTotal;
  }, 0);

  const base: BaseCartValue = {
    id: String(item.id ?? ''),
    name: String(item.name ?? ''),
    price: item.price ?? 0,
    quantity: item.quantity || 1,
    subtotal: total,
    total,
    cartId,
    specialInstructions: item.note ?? '',
    modifiers: mappedModifiers,
    note: item.note,
    availableForOrdering: item.availableForOrdering,
    isAgeRestricted: item.isAgeRestricted,
    isShippable: item.isShippable,
    ...(options?.menuId && { menuId: options.menuId }),
  };

  if (cartType === CartItemType.RETAIL) {
    return { key: cartId, value: { ...base, type: CartItemType.RETAIL } as RetailCartValue };
  }

  return { key: cartId, value: { ...base, type: CartItemType.RESTAURANT } as RestaurantCartValue };
}

/**
 * Adds an array of items to an existing cart in local storage.
 *
 * @template T - The specific type of cart item, extending either `RetailCartItem` or `RestaurantCartItem`.
 * @param cartKey - The local storage key identifying the target cart.
 * @param items - An array of items to append to the cart.
 */
export function addItemsToCart<T extends RetailCartItem | RestaurantCartItem>(
  cartKey: LocalStorageKey,
  items: T[],
) {
  const cart = getFromLocalStorage<T[]>(cartKey, []);
  const updatedCart = [...cart, ...items];
  saveToLocalStorage(cartKey, updatedCart);

  if (globalThis.window !== undefined) {
    globalThis.window.dispatchEvent(
      new CustomEvent("local-cart-updated", { detail: updatedCart })
    );
  }
}
